import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
	Button,
	Checkbox,
	Empty,
	Form,
	FormInstance,
	Input,
	Modal,
	Tooltip,
	Typography,
	notification,
} from 'antd';
import { blue } from '@ant-design/colors';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';

import { txt } from '../../../../locales/i18n';
import { Spinner } from '../../spinner';

import { ImageSelector } from '../../image-selector';

export type ImageCatalogueProps = {
	useVisible: [boolean, (state: boolean) => void];
	sourcePrefix: string;
	onSelectImage?: (imageName: string) => void;
	onQuery: (searchText: string) => Promise<string[]>;
	onCreate: (imageCreate: any) => Promise<void>;
	onRemove: (imageName: string) => Promise<void>;
	showTransparent?: boolean;
};

export const ImageCatalogue = (props: ImageCatalogueProps) => {
	const [visible, setVisible] = props.useVisible;
	const [imageNames, setImageNames] = useState<string[] | null>(null);
	const [selectedDeletionImage, setSelectedDeletionImage] = useState<string | null>(null);
	const [imageSource, setImageSource] = useState<string | null>(null);
	const [showAddImageForm, setShowAddImageForm] = useState(false);
	const [addingImage, setAddingImage] = useState(false);

	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const formRef = useRef<FormInstance>(null);

	useEffect(() => {
		props.onQuery('').then(setImageNames);
	}, [props, selectedDeletionImage]);

	useEffect(() => {
		if (visible === false) setImageSource(null);
	}, [visible]);

	useEffect(() => {
		if (addingImage) return;
		props.onQuery('').then(setImageNames);
	}, [props, addingImage]);

	const handleSearch = async (event: any) => {
		setImageNames(null);

		const searchTimeout = searchTimeoutRef.current;
		if (searchTimeout) clearTimeout(searchTimeout);

		searchTimeoutRef.current = setTimeout(async () => {
			const searchText = event.target.value;
			const imageNames = await props.onQuery(searchText);
			setImageNames(imageNames);
		}, 400);
	};

	const handleAddImage = async (event: any) => {
		setAddingImage(true);

		if (imageSource === null) {
			setAddingImage(false);
			const message = txt('notification.image missing');
			return notification.error({ message });
		}

		event.imageSource = imageSource;

		try {
			await props.onCreate(event);
			const message = txt('notification.create successful');
			notification.success({ message });
			setImageSource(null);
			formRef.current?.resetFields();
			return setAddingImage(false);
		} catch (error) {
			if (error instanceof Error && error.message === 'name already exists') {
				const message = txt('notification.name already exits');
				notification.error({ message });
				return setAddingImage(false);
			}

			const message = txt('notification.create failed');
			notification.error({ message });
			return setAddingImage(false);
		}
	};

	const handleRemoveImage = (event: React.MouseEvent, imageName: string) => {
		event.stopPropagation();
		setSelectedDeletionImage(imageName);
	};

	const handleSelectImage = (imageName: string) => {
		if (!props.onSelectImage) return;

		props.onSelectImage(imageName);
		setVisible(false);
	};

	return (
		<>
			<ModalStyled
				visible={visible}
				maskClosable
				onCancel={() => setVisible(false)}
				footer={null}
				closable={false}
				width={920}
			>
				<div className="modal-content-container">
					<div className="header-container">
						<Tooltip title={txt('global.search')}>
							<Input placeholder={txt('global.search')} onChange={handleSearch} />
						</Tooltip>

						<Tooltip title={showAddImageForm ? txt('global.close') : txt('global.add')}>
							<Button
								type="primary"
								danger={showAddImageForm}
								onClick={() => setShowAddImageForm((previous) => !previous)}
							>
								<FontAwesomeIcon icon={showAddImageForm ? faXmark : faPlus} />
							</Button>
						</Tooltip>
					</div>

					{showAddImageForm && (
						<Form
							ref={formRef}
							onFinish={handleAddImage}
							className="add-image-container"
						>
							<ImageSelector
								imageSource={imageSource}
								onSelect={setImageSource}
								onReset={() => setImageSource(null)}
								onError={() => setImageSource(null)}
								className="image-selector"
							/>

							<div className="add-image-bottom-container">
								<Tooltip title={txt('global.name')}>
									<Form.Item name="imageName">
										<Input required placeholder={txt('global.name')} />
									</Form.Item>
								</Tooltip>

								{props.showTransparent && (
									<Tooltip title={txt('global.transparent')}>
										<Form.Item
											name="transparent"
											initialValue={false}
											valuePropName="checked"
										>
											<Checkbox>{txt('global.transparent')}</Checkbox>
										</Form.Item>
									</Tooltip>
								)}

								<Tooltip title={txt('global.add')}>
									<Button type="primary" htmlType="submit" loading={addingImage}>
										<FontAwesomeIcon icon={faPlus} />
									</Button>
								</Tooltip>

								<Tooltip title={txt('global.clear')}>
									<Button
										danger
										type="primary"
										onClick={() => setImageSource(null)}
										loading={addingImage}
									>
										<FontAwesomeIcon icon={faTrash} />
									</Button>
								</Tooltip>
							</div>
						</Form>
					)}

					<div className="images-container">
						{imageNames === null && <Spinner />}

						{imageNames && (
							<>
								{imageNames.map((imageName) => (
									<div
										key={imageName}
										className="image-container"
										onClick={() => handleSelectImage(imageName)}
									>
										<Button
											danger
											onClick={(event) => handleRemoveImage(event, imageName)}
											type="primary"
											size="small"
											className="remove-button"
										>
											<FontAwesomeIcon icon={faTrash} />
										</Button>
										<img
											src={`${props.sourcePrefix}${imageName}`}
											alt={imageName}
										/>
										<Typography.Text>{imageName}</Typography.Text>
									</div>
								))}
							</>
						)}

						{imageNames?.length === 0 && <Empty />}
					</div>
				</div>
			</ModalStyled>

			<DeleteModal
				onConfirm={async () => await props.onRemove(selectedDeletionImage!)}
				useSelectedDeletionImage={[selectedDeletionImage, setSelectedDeletionImage]}
				sourcePrefix={props.sourcePrefix}
			/>
		</>
	);
};

type DeleteModalProps = {
	useSelectedDeletionImage: [string | null, (state: string | null) => void];
	onConfirm: () => void | Promise<void>;
	onDismiss?: () => void | Promise<void>;
	sourcePrefix: string;
};

function DeleteModal(props: DeleteModalProps): JSX.Element {
	const [selectedDeletionImage, setSelectedDeletionImage] = props.useSelectedDeletionImage;
	const [loading, setLoading] = useState<boolean>(false);
	const [confirmText, setConfirmText] = useState<string>('');

	const handleConfirm = async () => {
		setLoading(true);

		try {
			await props.onConfirm();
			notification.success({ message: txt('notification.deletion successful') });
			setSelectedDeletionImage(null);
		} catch (error) {
			notification.error({ message: txt('notification.deletion failed') });
		}

		setLoading(false);
	};

	const handleCancel = async () => {
		setSelectedDeletionImage(null);
		setConfirmText('');
		await props.onDismiss?.();
	};

	return (
		<DeleteModalStyled
			okText={<FontAwesomeIcon icon={faTrash} />}
			cancelText={txt('global.cancel')}
			maskClosable
			visible={selectedDeletionImage !== null}
			onCancel={handleCancel}
			okButtonProps={{
				disabled: confirmText.toLowerCase() !== txt('global.delete').toLowerCase(),
				danger: true,
			}}
			onOk={handleConfirm}
			confirmLoading={loading}
		>
			<div className="modal-content-container">
				<p> {txt('notification.confirm delete text')}</p>
				<Input
					value={confirmText}
					onChange={(event) => setConfirmText(event.target.value)}
				/>

				<div className="image-container">
					<img
						src={`${props.sourcePrefix}${selectedDeletionImage}`}
						alt={selectedDeletionImage!}
					/>

					<Typography.Text>{selectedDeletionImage}</Typography.Text>
				</div>
			</div>
		</DeleteModalStyled>
	);
}

const ModalStyled = styled(Modal)`
	.modal-content-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.header-container {
		display: flex;
		gap: 1rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid #ddd;
	}

	.images-container {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;

		overflow: scroll;
		max-height: 60vh;
		min-height: 20vh;

		.image-container {
			height: 200px;
			text-align: center;
			cursor: pointer;
			position: relative;
			display: flex;
			flex-direction: column;
			margin: 1rem;

			.remove-button {
				position: absolute;
				display: none;
				right: 0;
			}

			img {
				height: 100%;
				width: auto;
				object-fit: contain;
				box-sizing: border-box;
				border: 2px solid transparent;
			}

			&:hover {
				img {
					border-color: ${blue.primary};
				}

				.remove-button {
					display: block;
				}
			}
		}
	}

	.add-image-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		align-items: center;
		border-bottom: 1px solid #ddd;

		.image-selector {
			width: 480px;
			height: 300px;
		}

		.add-image-bottom-container {
			gap: 1rem;
			display: flex;
		}
	}
`;

const DeleteModalStyled = styled(Modal)`
	.modal-content-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.image-container {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 1rem;
		text-align: center;

		img {
			width: 100%;
			height: auto;
			max-height: 40vh;
			object-fit: contain;
			box-sizing: border-box;
		}
	}
`;
